import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Button, 
  Box, 
  Container,
  useScrollTrigger,
  Slide,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import Logo from './Logo';

interface HideOnScrollProps {
  children: React.ReactElement;
}

// Component to hide header on scroll down
const HideOnScroll = (props: HideOnScrollProps) => {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

const Header: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [elevateHeader, setElevateHeader] = useState(false);
  
  // Add shadow to header when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setElevateHeader(true);
      } else {
        setElevateHeader(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Logo />
      </Box>
      <List>
        <ListItem sx={{ justifyContent: 'center' }}>
          <Button 
            color="primary" 
            variant="text" 
            size="large" 
            sx={{ mb: 1 }}
            component={RouterLink}
            to="/login"
          >
            Войти
          </Button>
        </ListItem>
        <ListItem sx={{ justifyContent: 'center' }}>
          <Button 
            color="primary" 
            variant="contained" 
            size="large"
            component={RouterLink}
            to="/signup"
          >
            Регистрация
          </Button>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          color="transparent" 
          elevation={0}
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            boxShadow: elevateHeader ? '0 4px 20px rgba(0, 0, 0, 0.08)' : 'none',
            transition: 'box-shadow 0.3s ease'
          }}
        >
          <Container maxWidth="lg">
            <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
              <RouterLink to="/" style={{ textDecoration: 'none' }}>
                <Logo />
              </RouterLink>
              
              {isMobile ? (
                <IconButton
                  color="primary"
                  aria-label="open drawer"
                  edge="end"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              ) : (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    color="primary" 
                    variant="text" 
                    size="large"
                    component={RouterLink}
                    to="/login"
                    sx={{ 
                      fontWeight: 500,
                      '&:hover': {
                        backgroundColor: 'rgba(94, 53, 177, 0.04)'
                      }
                    }}
                  >
                    Войти
                  </Button>
                  <Button 
                    color="primary" 
                    variant="contained" 
                    size="large"
                    component={RouterLink}
                    to="/signup"
                    sx={{ fontWeight: 500 }}
                  >
                    Регистрация
                  </Button>
                </Box>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 240,
            borderRadius: '0 16px 16px 0'
          },
        }}
      >
        {drawer}
      </Drawer>
      
      {/* Toolbar placeholder to prevent content from hiding behind the appbar */}
      <Toolbar />
    </>
  );
};

export default Header; 